import { Box, Typography } from '@mui/material';
import React, { useState } from 'react';
import type BackendService from '../../../services/admin/BackendService';
import type { DocumentAsset } from '../../../services/admin/adminBackendServiceDocumentLibraryMixin';
import { getLastImportedBatch, recordImportedBatch } from './documentImportProgress';
import { useEditorSidebar } from './editorSidebarContext';
import DocumentViewerDialog from './DocumentViewerDialog';
import { DocumentsLibraryList, DocumentsPdfDropZone } from './MediaLibraryDocumentParts';
import { useDocumentLibrary } from './useDocumentLibrary';
import VideoLibrarySearchField from './VideoLibrarySearchField';

interface MediaLibraryDocumentsTabProps {
  fundOpportunityId: string;
  backendService: BackendService;
  isNarrow: boolean;
  modelType: string;
  modelId: string;
}

const isDeletable = (doc: DocumentAsset) =>
  doc.status === 'FAILED' || (doc.status === 'READY' && (doc.referenceCount ?? 0) === 0);

function deleteMessage(doc: DocumentAsset): string {
  const uses = doc.referenceCount ?? 0;
  if (uses === 0) return 'Delete this document from the library? This can\u2019t be undone.';
  return `This document is still referenced in ${uses} place${uses === 1 ? '' : 's'}. Delete it anyway?`;
}

function useDocumentDelete(remove: (id: string, force: boolean) => Promise<void>) {
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  return {
    confirmId,
    deleting,
    deleteError,
    setConfirmId,
    cancelDelete: () => {
      setConfirmId(null);
      setDeleteError(null);
    },
    confirmDelete: async (doc: DocumentAsset) => {
      setDeleting(true);
      setDeleteError(null);
      try {
        await remove(doc.externalId, (doc.referenceCount ?? 0) > 0);
        setConfirmId(null);
      } catch (err) {
        setDeleteError(
          (err as { error?: string; message?: string })?.error ??
            (err as Error)?.message ??
            'Could not delete this document.',
        );
      } finally {
        setDeleting(false);
      }
    },
  };
}

/**
 * The viewer for a library document. With no pick target, a long document imports in
 * page-limited batches and remembers where it stopped (see documentImportProgress).
 */
function LibraryDocumentViewer({
  documentAsset,
  backendService,
  modelType,
  modelId,
  onDone,
}: {
  documentAsset: DocumentAsset | null;
  backendService: BackendService;
  modelType: string;
  modelId: string;
  onDone: () => void;
}) {
  const { documentPickTarget, closeSidebar, importSections } = useEditorSidebar();
  const documentId = documentAsset?.externalId ?? null;
  return (
    <DocumentViewerDialog
      key={documentId ?? ''}
      open={!!documentAsset}
      documentAsset={documentAsset}
      pickTarget={documentPickTarget}
      modelType={modelType}
      modelId={modelId}
      backendService={backendService}
      autoAdvance={!documentPickTarget}
      resumeAfter={documentId ? getLastImportedBatch(modelType, modelId, documentId) : null}
      onImported={(sections, cropMeta) => {
        if (documentPickTarget) documentPickTarget.onImported(sections, cropMeta);
        else importSections(sections, cropMeta);
      }}
      onBatchImported={(range) => {
        if (documentId) recordImportedBatch(modelType, modelId, documentId, range);
      }}
      // Done importing: close the sidebar so the new content is in view, as before batching.
      onFinished={() => {
        onDone();
        closeSidebar();
      }}
      // Cancelled, or stopped with batches left: keep the sidebar open to carry on from it.
      onClose={() => {
        onDone();
        if (documentPickTarget) closeSidebar();
      }}
    />
  );
}

/**
 * The sidebar's Documents tab: the fund's document library — upload, search,
 * and the documents themselves.
 */
export default function MediaLibraryDocumentsTab({
  fundOpportunityId,
  backendService,
  isNarrow,
  modelType,
  modelId,
}: MediaLibraryDocumentsTabProps) {
  const library = useDocumentLibrary({ fundOpportunityId, backendService });
  const { documentPickTarget } = useEditorSidebar();
  const del = useDocumentDelete(library.remove);
  const [viewingExternalId, setViewingExternalId] = useState<string | null>(null);
  const viewingDoc = library.documents.find((d) => d.externalId === viewingExternalId) ?? null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">
        {documentPickTarget
          ? `Click a document to use it on ${documentPickTarget.label}.`
          : 'Import a document to choose a page, a page range, or part of a page.'}
      </Typography>
      <DocumentsPdfDropZone uploading={library.uploading} onUpload={library.upload} />
      {library.uploadError && (
        <Typography variant="caption" color="error.main">
          {library.uploadError}
        </Typography>
      )}
      <VideoLibrarySearchField value={library.search} onChange={library.setSearch} />
      {library.error && (
        <Typography variant="caption" color="error.main">
          {library.error}
        </Typography>
      )}
      <DocumentsLibraryList
        loading={library.loading}
        documents={library.documents}
        isNarrow={isNarrow}
        isDeletable={isDeletable}
        deleteMessage={deleteMessage}
        confirmId={del.confirmId}
        deleting={del.deleting}
        deleteError={del.deleteError}
        onRequestDelete={del.setConfirmId}
        onCancelDelete={del.cancelDelete}
        onConfirmDelete={(doc) => void del.confirmDelete(doc)}
        onViewDocument={setViewingExternalId}
      />
      <LibraryDocumentViewer
        documentAsset={viewingDoc}
        backendService={backendService}
        modelType={modelType}
        modelId={modelId}
        onDone={() => setViewingExternalId(null)}
      />
    </Box>
  );
}
